import React, { useState, useEffect } from "react"
import _ from "lodash"
import uuidV4 from "uuid/v4"

import keyService, * as chord from "data/key"
import automationService from "data/automation"
import { Colors } from "styles/app-styles"

type IAutomation = {
  activeTab?: number
  outsideTable?: boolean
  automationActive?: boolean
  editableFilters?: boolean
  includeSearchBar?: boolean
  automationContainerId?: string
  automationClass?: string
  tabOut?: Function
  shiftTabOut?: Function
}

const automationid = uuidV4()

// cell focus
const cellFocusStyle = `outline: 2px solid ${Colors.blackPearl}; outline-offset: -1px;`
const cellNoFocusStyle = ""

const findNestedInput = (node) => {
  if(node.tagName === "INPUT") return node
  else {
    for(let i = 0; i < node.children.length; i++) {
      let childNode = node.children[i]
      if(childNode.tagName === "INPUT") return childNode
      else return findNestedInput(childNode)
    }
  }
}

const findParentDropdownEditable = (ele, count = 0, BOUND = 8) => {
  if(ele && count < BOUND) {
    if(Array.from(ele.classList).includes("automated")) return ele
    else return findParentDropdownEditable(ele.parentElement, ++count)
  } else return null
}

const findParentRowAddRemove = (ele, count = 0, BOUND = 5) => {
  if(ele && count < BOUND) {
    let classList = Array.from(ele.classList)
    if(classList.includes('carousel-row-delete') || classList.includes('carousel-row-add')) return ele
    else return findParentRowAddRemove(ele.parentElement, ++count)
  } else return null
}

const findParentByClassName = (ele, className, count = 0, BOUND = 10) => {
  if(ele && count < BOUND) {
    let classList = Array.from(ele.classList)
    if(classList.includes(className)) return ele
    else return findParentRowAddRemove(ele.parentElement, className, ++count)
  } else return null
}

const targetIsChildOfNode = (targetEle: HTMLElement | null, tabableElement: HTMLElement, count: number = 0): boolean => {
  if(!targetEle) return false
  else if(count > 6) return false
  else if(targetEle.isSameNode(tabableElement)) return true
  else return targetIsChildOfNode(targetEle.parentElement, tabableElement, count++)
}

const updateValidation = _.debounce(() => {
  Array.from(document.querySelectorAll('.has-errors')).forEach(el => el.classList.remove('has-errors'))
  Array.from(document.querySelectorAll('[data-error]')).forEach(el => {
    const td = (function getPrimeReactTD(el){
      if (!el || el === document.body) return
      if (el.classList.contains('automated')) return el
      const parent = el.parentElement
      if (parent && parent.nodeName === 'TR') return el
      return getPrimeReactTD(parent!)
    }(el))
    if (td)
      return td.classList.add('has-errors')
    const child = el.children.length == 1 && el.children[0]
    if (el.tagName == 'SPAN' && child && child.tagName == 'DIV')
      return child.classList.add('has-errors')

    el.classList.add('has-errors')
  })
}, 300)

const CarouselAutomation = (props: IAutomation) => {
  const [automationActive, setAutomationActive] = useState<boolean>(props.automationActive === undefined ? true : props.automationActive)
  const [focusIndex, setFocusIndex] = useState<number>(0)

  const focusCell = (node) => {
    if(node && node.style) node.style.cssText += cellFocusStyle
    if(node && (_.includes(node.className, "p-dropdown") || _.includes(node.className, "p-checkbox"))) {
      const dd = findNestedInput(node)
      if(dd) {
        dd.focus()
        if(_.includes(node.className, "p-checkbox")) node.style.cssText += "border: 0px !important;"
      }
    } else if(node && _.includes(node.className, "p-editable-column")) {
      node.click()
    } else if (node) {
      node.focus()
      setTimeout(() => { node.select && node.select() }, 0)
    }
    updateValidation()
  }

  const getEditableNodes = (automationClass?: string) => {
    let primaryNodes: any[] = []
    if(props.automationClass) primaryNodes = Array.from(document.getElementsByClassName(props.automationClass))
    else if(automationClass) primaryNodes = Array.from(document.getElementsByClassName(automationClass))
    else primaryNodes = Array.from(document.getElementsByClassName("automated"))
    const secondaryNodes = Array.from(document.getElementsByClassName("automated-secondary"))
    // table header nodes need to be detected by normal automation
    // because they dont share the concept of view mode/editor mode
    let tableHeaderNodes = [], searchBar = []
    if(props.editableFilters) tableHeaderNodes = _.map(Array.from(document.querySelectorAll("th.p-filter-column")), i => i.children[0])
    if(props.includeSearchBar) searchBar = _.map(Array.from(document.querySelectorAll("#search-bar")), i => i)
    let editableNodes = primaryNodes.concat(secondaryNodes.concat(tableHeaderNodes.concat(searchBar)))
    editableNodes =
      _.filter(editableNodes, node => _.get(node, "nodeName") !== "TH" &&
      !(_.includes(_.get(node, "className"), "p-component") &&
      _.includes(_.get(node, "parentElement.className"), "p-cell-editing")) &&
      !_.includes(_.get(node, "className"), "p-disabled") &&
      node !== undefined)
    return editableNodes
  }

  const unfocusPrevious = (nodes) => {
    if(nodes[focusIndex] && (_.includes(nodes[focusIndex].className, "p-dropdown") || _.includes(nodes[focusIndex].className, "p-checkbox"))) {
      const dd = findNestedInput(nodes[focusIndex])
      _.invoke(dd, "blur")
    } else {
      _.invoke(nodes[focusIndex], "blur")
    }
    if(nodes[focusIndex]) nodes[focusIndex].setAttribute("style", cellNoFocusStyle)
  }

  const unfocusAll = (nodes) => {
    _.forEach(nodes, (n, i) => {
      const dd = findNestedInput(n)
      dd ? _.invoke(dd, "blur") : _.invoke(n, "blur")
      n.setAttribute("style", cellNoFocusStyle)
    });
  }

  const updateFocus = (nodes, newIndex) => {
    const node = nodes[newIndex]
    unfocusAll(nodes)
    focusCell(node)
    setFocusIndex(newIndex)
  }

  useEffect(() => {
    const isActive = props.automationActive === undefined ? true : props.automationActive
    setAutomationActive(isActive)
    if(isActive) {
      const editableNodes = getEditableNodes()
      updateFocus(editableNodes, focusIndex)
    } else {
      // Added 4/22 - Liz
      const editableNodes = getEditableNodes()
      unfocusAll(editableNodes)
    }
  }, [props.automationActive])

  useEffect(() => {
    const overrideKeyHandlers = e => {
      if(_.includes(["Tab"], e.key)) {
        e.preventDefault()
        return false
      }
    }

    const getHookTargets = () => {
      return _.filter(Array.from(document.querySelectorAll('[class*="automation_hook_T"]')), (h) => !h.className.includes('p-disabled'))
    }

    const getHookSources = () => {
      return _.filter(Array.from(document.querySelectorAll('[class*="automation_hook_S"]')), (h) => !h.className.includes('p-disabled'))
    }

    // Hooks need three different classNames to work:
    // 1. automation_hook_source_row_start on the first input of the source row
    // 2. automation_hook_S${i} on the input that should jump to the first hooked input after tab, where i is the row index
    // 3. automation_hook_T${i} on each of the hooked inputs, where i is the index of the source row
    const handleHooks = (editableNodes, tempFocus) => {
      const hookTargets = getHookTargets()
      let targetIdx = 0
      if(hookTargets.length) {
        const hookId = _.find(hookTargets[0].classList, (c) => _.includes(c, "automation_hook_T"))
        const hookNum = hookId.at(-1)
        const currentIdx = _.findIndex(editableNodes, (n) => _.includes(n.classList, `automation_hook_T${hookNum}`))
        targetIdx = _.add(_.findIndex(editableNodes, (n) => _.includes(n.classList, `automation_hook_S${hookNum}`)), 1)
        const prevHookTargetIdx = _.add(_.findIndex(editableNodes, (n) => _.includes(n.classList, `automation_hook_S${_.subtract(parseInt(hookNum), 1)}`)), 1)
        if(currentIdx !== targetIdx) {
          editableNodes = _.filter(editableNodes, (n) => {
            let target = _.find(n.classList, (c) => _.includes(c, "automation_hook_T"))
            if(target) return false
            else return true
          })
          editableNodes.splice(targetIdx, 0, ...hookTargets)
        }
        if((prevHookTargetIdx && focusIndex > prevHookTargetIdx && (_.includes(_.get(_.find(editableNodes, (n) => n === document.activeElement), "classList"), "automation_hook_source_row_start"))) ||
           tempFocus > editableNodes.length - 1) {
          tempFocus = prevHookTargetIdx
        }
      }
      return [editableNodes, tempFocus, targetIdx]
    }

    const automationSubscription = automationService.stream().subscribe(event => {
      let [editableNodes, tempFocus, targetIdx] = handleHooks(getEditableNodes(), focusIndex)
      switch(true) {
        case event.type === "REGISTER":
          if(automationid === event.data) {
            setAutomationActive(true)
          } else {
            setAutomationActive(false)
          }
          break
        case event.type === "REFOCUS":
          if(tempFocus < editableNodes.length) updateFocus(editableNodes, tempFocus)
          else updateFocus(editableNodes, editableNodes.length - 1)
          break
        case event.type === "OFFSET_FOCUS_INDEX":
          setFocusIndex(focusIndex + event.data)
          break
        case event.type === "OFFSET_FOCUS":
          const newFocus = focusIndex + event.data
          setFocusIndex(newFocus)
          updateFocus(editableNodes, newFocus)
          break
        case event.type === "FOCUS_DOM_NODE":
          const node = event.data
          const newIdx = editableNodes.findIndex(n => n.isEqualNode(node))
          if(newIdx >= 0) updateFocus(editableNodes, newIdx)
          break
        case event.type === "CAROUSEL_TAB_OUT":
          unfocusAll(editableNodes)
          props.tabOut && props.tabOut()
          setTimeout(() => automationService.broadcast("MODAL_TABBING", {}), 0)
          break;
        case event.type === "CAROUSEL_STATE_CHANGE":
          setFocusIndex(0);
          updateFocus(editableNodes, 0)
          break
        case event.type === 'CAROUSEL_ROW_ADD_REMOVE':
          const rowIndex             = event.data['i'];
          const container            = event.data['container']
          const rowContainer         = event.data['rowContainer']
          const operation            = _.get(event, 'data.op')
          const allMultiRows         = document.getElementsByClassName('carousel-multi-row')
          const allMultiRowNodes     = document.querySelectorAll('.carousel-multi-row .automated');
          let   currentMultiRow      = container.getElementsByClassName('carousel-multi-row');
          let   currentMultiRowNodes = _.filter(container.querySelectorAll('.carousel-multi-row .automated'), (n) => !n.className.includes('p-disabled'))
          const rows                 = container.getElementsByClassName("carousel-row-add").length;
          const inputsPerRow         = Math.round(currentMultiRowNodes.length / rows)
          let   offset               = editableNodes.length - currentMultiRowNodes.length;

          // Special, not so pretty logic currently only on behalf of vacancy/credit loss having multiple multi row sets of inputs and a toggle checkbox to show/hide a multi row set of inputs //
          if (window.location.href.includes('n/property-market/vacancy-credit-loss') && !container.innerHTML.includes('Percentage Overrides')){
            let n = 0
            if(operation === 'ADD') n = currentMultiRowNodes[rowIndex+1]
            else n = currentMultiRowNodes[Math.max(rowIndex-1, 0)]

            let newIndex = editableNodes.findIndex(node => node.isEqualNode(n))
            updateFocus(editableNodes, newIndex)
            break
          }

          if(getHookTargets().length){
            const nodesPerRow = currentMultiRowNodes.length / currentMultiRow.length
            let newRowStartIdx = rowIndex * nodesPerRow
            if(rowContainer.querySelector(".automation_hook_source_row_start")) {
              let rowStartNodes = container.querySelectorAll(".automation_hook_source_row_start")
              let focusNode = rowStartNodes[0]
              if(operation === 'ADD') {
                focusNode = rowStartNodes[rowIndex + 1]
              } else {
                focusNode = rowStartNodes[Math.max(rowIndex - 1, 0)]
              }
              newRowStartIdx = editableNodes.findIndex(node => node.isEqualNode(focusNode))
            } else {
              let firstNodeInDetailRow = container.querySelector('input')
              let firstNodeInDetailRowIdx = editableNodes.findIndex(node => node.isEqualNode(firstNodeInDetailRow))
              if(operation === 'ADD') {
                newRowStartIdx = firstNodeInDetailRowIdx + newRowStartIdx + nodesPerRow
              } else {
                newRowStartIdx = firstNodeInDetailRowIdx + Math.max(newRowStartIdx - nodesPerRow, 0)
              }
            }
            updateFocus(editableNodes, newRowStartIdx)
          } else if (operation === 'ADD'){
            let newIndex = ((rowIndex + 1) * inputsPerRow)
            if(getHookSources().length) {
              let subset = editableNodes.slice(newIndex, editableNodes.length - 1)
              let node = _.find(subset, (s) => s.className.includes('automation_hook_source_row_start'))
              newIndex = _.findIndex(editableNodes, (n) => n.isEqualNode(node))
            } else newIndex = newIndex + offset
            updateFocus(editableNodes, newIndex);
          } else {
            let newIndex = ((rowIndex - 1) * inputsPerRow)
            if(getHookSources().length) {
              let subset = editableNodes.slice(newIndex, editableNodes.length - 1)
              let node = _.find(subset, (s) => s.className.includes('automation_hook_source_row_start'))
              newIndex = _.findIndex(editableNodes, (n) => n.isEqualNode(node))
            } else newIndex = newIndex + offset
            rowIndex !== 0 ? updateFocus(editableNodes, newIndex) : updateFocus(editableNodes, tempFocus);
          }
          break
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      if(!automationActive) return

      let newIndex = 0
      let [editableNodes, tempFocus] = handleHooks(getEditableNodes(), focusIndex)

      switch(true) {
        case k.chord === chord.TAB && k.down === true:
          newIndex = tempFocus + 1
          if(newIndex < editableNodes.length) {
            updateFocus(editableNodes, newIndex)
          } else {
            if(props.outsideTable) {
              if(props.tabOut) {
                unfocusPrevious(editableNodes)
                props.tabOut()
              } else {
                updateFocus(editableNodes, 0)
              }
              break
            }
            unfocusPrevious(editableNodes)
            // need to use settimeout to jump the callstack so that the dom blur will actually trigger any react handler
            setTimeout(() => automationService.broadcast("MODAL_TABBING", {}), 0)
          }
          break
        case k.chord === chord.SHIFT_TAB && k.down === true:
          newIndex = tempFocus - 1
          if(newIndex >= 0) {
            updateFocus(editableNodes, newIndex)
          } else {
            if(props.outsideTable) {
              if(props.shiftTabOut) {
                unfocusPrevious(editableNodes)
                props.shiftTabOut()
              } else {
                updateFocus(editableNodes, editableNodes.length - 1)
              }
              break
            }
            unfocusPrevious(editableNodes)
            // need to use settimeout to jump the callstack so that the dom blur will actually trigger any react handler
            setTimeout(() => automationService.broadcast("MODAL_SHIFT_TABBING", {}), 0)
          }
          break
        // case k.chord === chord.ENTER && k.down === true:
        //   updateFocus(editableNodes, tempFocus)
        //   break
        case k.chord === chord.ESC && k.down === true:
          unfocusPrevious(editableNodes)
          break
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          updateFocus(editableNodes, tempFocus) // blur and reselect the current node to persist value
          setTimeout(() => automationService.broadcast("HOTKEY_ADD_ROW", {}), 0) // dispatch intent to row-add-remove
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          updateFocus(editableNodes, tempFocus) // blur and reselect the current node to persist value
          setTimeout(() => automationService.broadcast("HOTKEY_REMOVE_ROW", {}), 0) // dispatch intent to row-add-remove
          break
      }
    })

    const clickSync = e => {
      e.stopPropagation()
      if(!e.isTrusted) return

      // Ignore clicks on on detailed route record caret up/down icons
      const detailedRouteRecordDown = document.getElementById('detailedRouteRecordDown');
      const detailedRouteRecordUp = document.getElementById('detailedRouteRecordUp')
      if (e.target === detailedRouteRecordDown || e.target === 'detailedRouteRecordUp' || detailedRouteRecordUp?.contains(e.target) || detailedRouteRecordDown?.contains(e.target)) {
        return
      }

      const automationContainer = document.getElementById(props.automationContainerId || "")
      let [editableNodes, tempFocus] = handleHooks(getEditableNodes(), focusIndex)

      let target_rect = e.target.getBoundingClientRect()
      if(e.target && e.target.className && _.isString(e.target.className) && (e.target.className.includes("p-dropdown") || e.target.className.includes('p-checkbox'))){
        if(_.includes(_.get(e, "target.offsetParent.className"), "p-filter-column")) {
          target_rect = _.get(e, "target.offsetParent")?.getBoundingClientRect()
        } else {
          const parentDD = findParentDropdownEditable(e.target)
          if(parentDD) target_rect = parentDD.getBoundingClientRect()
        }
      }

      let n_idx = -1
      _.forEach(editableNodes, (n, i) => {
        let n_rect = n.getBoundingClientRect()
        if(-2 <= n_rect.x - target_rect.x &&
            n_rect.x - target_rect.x <= 2 &&
            -2 <= n_rect.y - target_rect.y &&
            n_rect.y - target_rect.y <= 2 &&
            -2 <= n_rect.width - target_rect.width &&
            n_rect.width - target_rect.width <= 2 &&
            -2 <= n_rect.height - target_rect.height &&
            n_rect.height - target_rect.height <= 2)
        {
          n_idx = i
          return false
        } else if(-2 <= n_rect.x - target_rect.x &&
          n_rect.x - target_rect.x <= 2 &&
          -2 <= n_rect.y - target_rect.y &&
          n_rect.y - target_rect.y <= 2 &&
          target_rect.width === 0 &&
          target_rect.height === 0)
        {
          n_idx = i
          return false
        } else if(targetIsChildOfNode(e.target, n)) {
          n_idx = i
          return false
        }
      })

      if(n_idx >= 0) {
        // && automationActive added to below line 4/22 - Liz
        if(n_idx === tempFocus && automationActive) return
        automationService.broadcast("REGISTER", automationid)
        setTimeout(() => updateFocus(editableNodes, n_idx), 0)
      } else {
        // if user is clicking on row add/remove controls, do nothing
        if(findParentRowAddRemove(e.target)) {
          return
        }
        // if user clicks inside automation container, but not on a control, focus on the first selectable control
        if(automationContainer && automationContainer.contains(e.target)) {
          setTimeout(() => updateFocus(editableNodes, 0), 0)
        }
        // otherwise, if user clicks outside automation container, give up control of automation
        unfocusAll(editableNodes)
        automationService.broadcast("UNREGISTER", automationid)
      }
    }

    document.addEventListener("keydown", overrideKeyHandlers)
    document.addEventListener("select", clickSync)
    document.addEventListener("click", clickSync)

    updateValidation()
    return function cleanup() {
      automationSubscription.unsubscribe()
      keySubscription.unsubscribe()
      document.removeEventListener("keydown", overrideKeyHandlers)
      document.removeEventListener("select", clickSync)
      document.removeEventListener("click", clickSync)
    }
  }, [focusIndex, props.activeTab, props.outsideTable, automationActive])

  return null
}

export default CarouselAutomation
